// cspell:ignore authtoken googleauthtoken
import { pickBy } from 'lodash';

import { ContactChannel, ContactData } from 'shared/models/contact';
import { EmailTemplateView } from 'shared/models/email-templates';
import { NoteView } from 'shared/models/note';
import { SearchResultsViewType } from 'shared/models/search';
import { SearchConfig } from 'shared/models/search';
import { SequenceVariables } from 'shared/models/sequence';
import { SequenceRecipientCustomStage } from 'shared/models/sequence-recipient';
import { UserData } from 'shared/models/user';
import { HrefFilePayload } from 'shared/types/file-payload';

import { handleSessionExpired } from './common/session-expired';
import { queryString, sleep } from './common/utils';
import { ChartData, TableData } from './components/agent/agent-types';
import { config } from './config';
import { store } from './configure-store';
import { Client, ContactType, Person, Search, SearchResult } from './state';
import { RawJsonProfile } from './types/li-json-profile';

export const ApiEndpoint = config.ApiEndpoint;

const deferredRequestPollIntervalMs = 1000;
const deferredRequestMaxPollIntervalMs = 5000;
const deferredRequestMinPollIntervalMs = 100;

const getAuthHeaders = (): any => {
    const session = store.getState().session;
    return session.authToken ? { authtoken: session.authToken } : {};
};

export const getHeaders = () => {
    const headers: { [key: string]: string } = {
        Accept: 'application/json',
        'Content-Type': 'application/json'
    };
    return Object.assign({}, headers, getAuthHeaders());
};

export const getDeferredRequestResult = (id: string) => {
    const endpoint = `${ApiEndpoint}/deferred-request/${id}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const parseResponse = (response: Response): Promise<{ data: any; success: boolean }> => {
    const unauthorized = 401;
    if (response.status === unauthorized) {
        handleSessionExpired();
    }
    return response
        .json()
        .then((json) => {
            // if request response is deferred, wait for polling interval and request the result
            // if request is still not completed, the response will be deferred again
            if (json.requestDeferred) {
                const pollIntervalMs =
                    typeof json.pollIntervalMs === 'number' ? json.pollIntervalMs : deferredRequestPollIntervalMs;
                const pollIntervalMsClamped = Math.min(
                    deferredRequestMaxPollIntervalMs,
                    Math.max(deferredRequestMinPollIntervalMs, pollIntervalMs)
                );
                return sleep(pollIntervalMsClamped).then(() => getDeferredRequestResult(json.deferredRequestId));
            } else {
                return {
                    data: json,
                    success: response.ok
                };
            }
        })
        .catch(() => {
            return { data: null, success: false };
        });
};

export const authenticate = (googleauthtoken: string, authtoken: string) => {
    const endpoint = `${ApiEndpoint}/authenticate`;
    const headers = pickBy({ googleauthtoken, authtoken });
    return fetch(endpoint, { headers } as any).then(parseResponse);
};

export const fetchUserInfo = (id: string) => {
    const endpoint = `${ApiEndpoint}/user/${id}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchJobs = () => {
    const endpoint = `${ApiEndpoint}/jobs`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchExtensionJobs = () => {
    const endpoint = `${ApiEndpoint}/extension-jobs`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const getJobMetrics = (ids: string[]) => {
    const endpoint = `${ApiEndpoint}/jobs/get-job-metrics`;
    return fetch(endpoint, {
        body: JSON.stringify({ ids }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const createJob = (params: {}) => {
    const endpoint = `${ApiEndpoint}/job`;
    return fetch(endpoint, {
        body: JSON.stringify(params),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const uploadClientLogo = (clientId: string, payload: HrefFilePayload) => {
    const endpoint = `${ApiEndpoint}/client/${clientId}/upload-logo`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const uploadBankTransactions = (bankAccountId: string, payload: HrefFilePayload) => {
    const endpoint = `${ApiEndpoint}/bank-account/${bankAccountId}/upload-transactions`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const createCBListPreset = (presetName: string, shared: boolean, payload: HrefFilePayload) => {
    const endpoint = `${ApiEndpoint}/searches/upload-cb-list-preset`;
    return fetch(endpoint, {
        body: JSON.stringify({ presetName, shared, payload }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const uploadJobOnePager = (jobId: string, payload: HrefFilePayload) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/upload-one-pager`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const generateJobOnePager = (jobId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/generate-one-pager`;
    return fetch(endpoint, { headers: getHeaders(), method: 'post' }).then(parseResponse);
};

export const fetchJobInfo = (id: string) => {
    const endpoint = `${ApiEndpoint}/job/${id}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchEmailTemplates = (group: string) => {
    const qs = queryString({ group });
    const endpoint = `${ApiEndpoint}/email-templates?${qs}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const updateEmailTemplate = (data: Partial<EmailTemplateView>) => {
    const endpoint = `${ApiEndpoint}/email-templates/${data.id}`;
    return fetch(endpoint, {
        body: JSON.stringify({ data }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const createEmailTemplate = (data: Partial<EmailTemplateView>) => {
    const endpoint = `${ApiEndpoint}/email-templates`;
    return fetch(endpoint, {
        body: JSON.stringify({ data }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const deleteTemplate = (id: number) => {
    const endpoint = `${ApiEndpoint}/email-templates/${id}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'delete'
    });
};

export const fetchJobDetails = (id: string) => {
    const endpoint = `${ApiEndpoint}/job/${id}?candidates=true`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchCandidate = (jobId: string, personId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const getEmailCounts = (jobId: string, email: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/account-email-counts?email=${email}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const updateCandidateStage = (personId: string, jobId: string, stage: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}/stage`;
    return fetch(endpoint, {
        body: JSON.stringify({ stage }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const getSchedulingDrafts = (
    personId: string,
    jobId: string,
    linkType: 'calendar_only' | 'astra_only' | 'both'
) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}/scheduling-drafts?${queryString({ linkType })}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const updateCandidateQualification = (personId: string, jobId: string, qualify: boolean, reason: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}/qualify`;
    return fetch(endpoint, {
        body: JSON.stringify({ reason, qualify }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const fetchClients = () => {
    const endpoint = `${ApiEndpoint}/clients`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const createClient = (params: {}) => {
    const endpoint = `${ApiEndpoint}/client`;
    return fetch(endpoint, {
        body: JSON.stringify(params),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const reprocessRecording = (recordingId: string, force: boolean) => {
    const body = JSON.stringify({ force });
    const endpoint = `${ApiEndpoint}/client/${recordingId}/reprocess-recording`;
    return fetch(endpoint, { headers: getHeaders(), method: 'post', body }).then(parseResponse);
};

export const uploadClientFiles = (clientId: string, payload: HrefFilePayload[]) => {
    const endpoint = `${ApiEndpoint}/client/${clientId}/files`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const uploadEditorImage = (payload: HrefFilePayload | { url: string }) => {
    const endpoint = `${ApiEndpoint}/upload-file`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const removeClientFile = (clientId: string, key: string) => {
    const endpoint = `${ApiEndpoint}/client/${clientId}/file`;
    return fetch(endpoint, {
        body: JSON.stringify({ key }),
        headers: getHeaders(),
        method: 'delete'
    }).then(parseResponse);
};

export const fetchPersonsList = (ids: string[]) => {
    const endpoint = `${ApiEndpoint}/get-persons`;
    return fetch(endpoint, {
        body: JSON.stringify({ ids }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchPerson = (id: string, jobId?: string) => {
    const jobQuery = jobId ? `?jobId=${jobId}` : '';
    const endpoint = `${ApiEndpoint}/person/${id}/details${jobQuery}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchPersonCrunchbaseData = (personId: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/crunchbase`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchPersonContacts = (personId: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/contacts`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const createPersonJobLabel = (personId: string, jobId: string, label: boolean) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/job/${jobId}/label`;
    return fetch(endpoint, {
        body: JSON.stringify({ label }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const createNote = (payload: Partial<NoteView>) => {
    const endpoint = `${ApiEndpoint}/notes`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchNotes = (notable: string) => {
    const endpoint = `${ApiEndpoint}/notes/${notable}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const deleteNote = (id: string) => {
    const endpoint = `${ApiEndpoint}/note/${id}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'delete'
    }); // no response
};

export const fetchUserActions = (target: string) => {
    const endpoint = `${ApiEndpoint}/actions/${target}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const addContact = (personId: string, value: string, channel: ContactChannel) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/add-contact`;
    return fetch(endpoint, {
        body: JSON.stringify({ value, channel }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const updateContact = (
    personId: string,
    value: string,
    channel: string,
    attribute: string,
    attrVal: boolean | ContactType
) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/update-contact`;
    return fetch(endpoint, {
        body: JSON.stringify({ value, channel, attribute, attrVal }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const deleteContact = (personId: string, channel: string, value: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/delete-contact`;
    return fetch(endpoint, {
        body: JSON.stringify({ personId, channel, value }),
        headers: getHeaders(),
        method: 'delete'
    }).then(parseResponse);
};

export const addContactFromMessage = (personId: string, account: string, messageId: string, value: string) => {
    const endpoint = `${ApiEndpoint}/communication/${messageId}/account/${account}/add-contact-from-message`;
    return fetch(endpoint, {
        body: JSON.stringify({ value, messageId, account, personId }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const uploadPersonFile = (personId: string, payload: HrefFilePayload[]) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/upload-file`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const updatePerson = (id: string, updates: Partial<Person>) => {
    const endpoint = `${ApiEndpoint}/person/${id}`;
    return fetch(endpoint, {
        body: JSON.stringify({ updates }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const doNotEmailPerson = (personId: string, payload: { note: string; clientId: string }) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/do-not-email-again`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const personNotInterestedAtThisTime = (
    personId: string,
    payload: {
        jobId: string;
        note: string;
        optOutUntil: number;
    }
) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/not-interested-at-this-time`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchUsers = () => {
    const endpoint = `${ApiEndpoint}/users`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchCandidatesForOutreach = (jobId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/download-candidates`;
    return fetch(endpoint, { headers: getHeaders(), method: 'put' }).then(parseResponse);
};

export const fetchRevivalCandidates = (jobId: string, disqualReason?: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/revive-candidates${
        disqualReason ? `?disqualReason=${disqualReason}` : ''
    }`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const addEmailAccount = (userId: string, jobId: string, capacity: number) => {
    const endpoint = `${ApiEndpoint}/email-accounts`;
    return fetch(endpoint, {
        body: JSON.stringify({ userId, jobId, capacity }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchClientById = (clientId: string) => {
    const endpoint = `${ApiEndpoint}/client/${clientId}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const updateClient = (clientId: string, updates: Partial<Client>) => {
    const endpoint = `${ApiEndpoint}/client/${clientId}/update`;
    return fetch(endpoint, {
        body: JSON.stringify({ updates }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const uploadClientPersonBlacklist = (clientId: string, files: string[]) => {
    const endpoint = `${ApiEndpoint}/client/${clientId}/upload-person-blacklist`;
    return fetch(endpoint, {
        body: JSON.stringify({ files }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchWeeklyUpdateData = (clientId: string, startDate: number, endDate: number, all: boolean) => {
    const requestAll = all ? `&all=true` : '';
    const endpoint =
        `${ApiEndpoint}/client/${clientId}/weekly-update?` + `startDate=${startDate}&endDate=${endDate}${requestAll}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const addCandidateToJob = (
    personId: string,
    jobId: string,
    source: string,
    stage: string,
    inMailSent: boolean
) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/add-candidate/${personId}`;
    return fetch(endpoint, {
        body: JSON.stringify({ source, stage, inMailSent }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const addPersonAndCreateCandidate = (
    jobId: string,
    data: { firstName: string; lastName: string; email: string }
) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/add-person-and-candidate`;
    return fetch(endpoint, {
        body: JSON.stringify(data),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const requestSendSequenceToPerson = (
    personId: string,
    jobId: string,
    account: string,
    sequenceId: string,
    variables: SequenceVariables,
    customStages: SequenceRecipientCustomStage[]
) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}/send-sequence-to-person`;
    return fetch(endpoint, {
        body: JSON.stringify({ account, customStages, sequenceId, variables }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const moveInGmail = (account: string, threadId: string, action: string) => {
    const endpoint = `${ApiEndpoint}/communication/move-in-gmail`;
    return fetch(endpoint, {
        body: JSON.stringify({
            account,
            action,
            threadId
        }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const refreshMessage = (messageId: string, account: string) => {
    const endpoint = `${ApiEndpoint}/communication/${messageId}/account/${account}`;
    return fetch(endpoint, {
        headers: getHeaders()
    }).then(parseResponse);
};

export const reassignClient = (threadId: string, account: string, newClientId: string) => {
    const endpoint = `${ApiEndpoint}/communication/${threadId}/reassign-client`;
    return fetch(endpoint, {
        body: JSON.stringify({ threadId, clientId: newClientId, account }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const parseProfile = (payload: {
    jobId: string;
    data: { url: string; json?: RawJsonProfile; html?: string; pendingLoadSections: number };
}) => {
    const endpoint = `${ApiEndpoint}/person/parse-profile`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getShortUrl = (longUrl: string) => {
    const endpoint = `${ApiEndpoint}/users/get-short-url`;
    return fetch(endpoint, {
        body: JSON.stringify({ longUrl }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const scoreProfile = (payload: any) => {
    const endpoint = `${ApiEndpoint}/person/score-profile`;
    return fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchAllEmailAccountInfo = () => {
    const endpoint = `${ApiEndpoint}/email-accounts`;
    return fetch(endpoint, {
        headers: getHeaders()
    }).then(parseResponse);
};

export const updateEmailAccount = (
    email: string,
    updates: { userId?: string; jobId?: string; syncStatus?: string }
) => {
    const endpoint = `${ApiEndpoint}/admin/update-email-account/${email}`;
    return fetch(endpoint, {
        body: JSON.stringify(updates),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const fetchScoringEvalData = (evalId?: string) => {
    const qs = evalId ? queryString({ evalId }) : '';
    const endpoint = `${ApiEndpoint}/get-scoring-eval-data?${qs}`;
    return fetch(endpoint, {
        headers: getHeaders()
    }); // do not parse
};

export const fetchSearchStats = (searchId: string) => {
    const endpoint = `${ApiEndpoint}/searches/search/${searchId}/stats`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const fetchSearchResults = (
    search: Partial<Search>,
    resultsType: SearchResultsViewType,
    isCampaign: boolean,
    limit?: number
) => {
    const endpoint = `${ApiEndpoint}/search/get-results`;
    return fetch(endpoint, {
        body: JSON.stringify({ search, resultsType, limit, isCampaign }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const fetchSearchResultsPreview = (
    search: Partial<Search>,
    resultsType: SearchResultsViewType,
    limit?: number
) => {
    const endpoint = `${ApiEndpoint}/search/preview-results`;
    return fetch(endpoint, {
        body: JSON.stringify({ search, resultsType, limit }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getPreviewCount = (search: Partial<Search>) => {
    const endpoint = `${ApiEndpoint}/search/preview-results`;
    return fetch(endpoint, {
        body: JSON.stringify({ search, count: true }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getSQLForConfig = (searchConfig: SearchConfig, jobId: string, searchId?: string) => {
    const endpoint = `${ApiEndpoint}/search/get-sql-for-config`;
    return fetch(endpoint, {
        body: JSON.stringify({ config: searchConfig, jobId, searchId }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const deleteSearch = (id: string) => {
    const endpoint = `${ApiEndpoint}/search/${id}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'delete'
    }); // no response
};

export const initializeSearch = (search: Partial<Search>) => {
    const endpoint = `${ApiEndpoint}/search/initialize`;
    return fetch(endpoint, {
        body: JSON.stringify({ search }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const updateSearch = (id: string, updates: Partial<Search>) => {
    const endpoint = `${ApiEndpoint}/search/${id}`;
    return fetch(endpoint, {
        body: JSON.stringify({ updates }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const getSearchProfiles = (id: string, personIds: string[]) => {
    const endpoint = `${ApiEndpoint}/search/${id}/get-profiles`;
    return fetch(endpoint, {
        body: JSON.stringify({ personIds }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getSearchForSourcing = (jobId: string, sortRank: number, direction: 'previous' | 'next') => {
    const endpoint = `${ApiEndpoint}/search/job/${jobId}/source/${sortRank}?direction=${direction}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const addSearchResultToJob = (jobId: string, result: SearchResult, disqualified: boolean) => {
    const endpoint = `${ApiEndpoint}/search/add-candidate/${jobId}`;
    return fetch(endpoint, {
        body: JSON.stringify({ result, disqualified }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const fetchSearchResultPersonAndCandidate = (result: SearchResult, jobId: string) => {
    const endpoint = `${ApiEndpoint}/search-result/person-candidate/${result.personId}/job/${jobId}`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const getSimilaritySearchResults = (searchQuery: string, searchType: string, topn: number) => {
    const endpoint = `${ApiEndpoint}/search/similarity`;
    return fetch(endpoint, {
        body: JSON.stringify({ searchQuery, searchType, topn }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const purgeSearch = (id: string) => {
    const endpoint = `${ApiEndpoint}/search/${id}/purge`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'post'
    }); // no response
};

export const refreshSearch = (id: string) => {
    const endpoint = `${ApiEndpoint}/search/${id}/refresh`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const purgeJobCandidates = (id: string) => {
    const endpoint = `${ApiEndpoint}/job/${id}/purge`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'post'
    }); // no response
};

export const downloadFile = (url: string) => {
    const endpoint = `${ApiEndpoint}/get-file`;
    return fetch(endpoint, {
        body: JSON.stringify({ url }),
        headers: getHeaders(),
        method: 'post'
    }); // no parse - send raw data through
};

export const getProfileDataSource = (personId: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/profile-data-source`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const getInferenceData = (inferenceId: string) => {
    const endpoint = `${ApiEndpoint}/inference/${inferenceId}`;
    return fetch(endpoint, {
        headers: getHeaders()
    }).then(parseResponse);
};

export const requestPresets = () => {
    const endpoint = `${ApiEndpoint}/search/presets`;
    return fetch(endpoint, {
        headers: getHeaders()
    }).then(parseResponse);
};

export const savePreset = (searchConfig: SearchConfig, name: string, group: string, groupLabel: string) => {
    const endpoint = `${ApiEndpoint}/search/save-preset`;
    return fetch(endpoint, {
        body: JSON.stringify({ config: searchConfig, name, group, groupLabel }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const sendTestEmail = (subject: string, body: string) => {
    const endpoint = `${ApiEndpoint}/email-templates/send-test-email`;
    return fetch(endpoint, {
        body: JSON.stringify({ body, subject }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getJobsSearchesStats = (refresh: boolean) => {
    const refreshQuery = refresh ? `?refresh=${refresh}` : '';
    const endpoint = `${ApiEndpoint}/search/stats${refreshQuery}`;
    return fetch(endpoint, {
        headers: getHeaders()
    }).then(parseResponse);
};

export const addWebsite = (personId: string, value: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/add-website`;
    return fetch(endpoint, {
        body: JSON.stringify({ value }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const getJobEmails = (jobId: string) => {
    const endpoint = `${ApiEndpoint}/email/job/${jobId}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const getFlaggedEmails = (account: string, offset: number, limit: number) => {
    const endpoint = `${ApiEndpoint}/communication/${account}/flagged?offset=${offset}&limit=${limit}`;
    return fetch(endpoint, { headers: getHeaders(), method: 'get' }).then(parseResponse);
};

export const syncAccountAndGetCommunications = (account: string, personId: string) => {
    const endpoint = `${ApiEndpoint}/email/${account}/person-comms-with-sync/${personId}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const getPersonCommunications = (id: string) => {
    const endpoint = `${ApiEndpoint}/person/${id}/communications`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const fetchFlaggedEmailAccounts = () => {
    const endpoint = `${ApiEndpoint}/email-accounts/flagged-accounts`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const requestForwardFlaggedEmail = (account: string, messageId: string, email: string) => {
    const endpoint = `${ApiEndpoint}/communication/${account}/forward-flagged-email/${messageId}`;
    return fetch(endpoint, {
        body: JSON.stringify({ email }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const reportIncorrectCandidate = (account: string, messageId: string) => {
    const endpoint = `${ApiEndpoint}/communication/${messageId}/account/${account}/report-incorrect-candidate`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const requestSetEmailMatch = (
    account: string,
    messageId: string,
    data: { ignored?: boolean; personIds?: string[]; jobIds?: string[]; error?: boolean }
) => {
    const endpoint = `${ApiEndpoint}/communication/${account}/set-match/${messageId}`;
    return fetch(endpoint, {
        body: JSON.stringify(data),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const requestAddSenderToIgnoredList = (account: string, messageId: string, data: { regexPattern: string }) => {
    const endpoint = `${ApiEndpoint}/communication/${account}/add-sender-to-ignored-list/${messageId}`;
    return fetch(endpoint, {
        body: JSON.stringify(data),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const requestCommunicationMatch = (account: string, threadId: string) => {
    const endpoint = `${ApiEndpoint}/communication/${account}/re-match/${threadId}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const fetchJobCandidatesSummary = (jobId: string, stages: string[]) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate-summary`;
    return fetch(endpoint, {
        body: JSON.stringify({ stages }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const requestSubmissionNoteStatusUpdate = (note: NoteView) => {
    const endpoint = `${ApiEndpoint}/note/${note.id}/request-submission-note-status`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getGeneratedSubmission = (jobId: string, personId: string, userId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/person/${personId}/user/${userId}/generate-submission`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const getRefinedSubmission = (
    episodeId: string,
    messages: Array<{ content: string; role: 'user' | 'system' | 'assistant' }>
) => {
    const endpoint = `${ApiEndpoint}/job/refine-submission`;
    return fetch(endpoint, {
        body: JSON.stringify({ messages, episodeId }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const trackSubmissionRefinements = (
    episodeId: string,
    userId: string,
    personId: string,
    jobId: string,
    count: number,
    skipped: boolean
) => {
    const endpoint = `${ApiEndpoint}/job/track-submission-refinements`;
    return fetch(endpoint, {
        body: JSON.stringify({ episodeId, userId, personId, jobId, count, skipped }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const recordJDUpdateRating = (inferenceId: string, rating: boolean, comment?: string) => {
    const endpoint = `${ApiEndpoint}/job/record-jd-update-rating`;
    return fetch(endpoint, {
        body: JSON.stringify({ inferenceId, rating, comment }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getAIEmailResponse = (personId: string, jobId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}/ai-email-response`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const crossAddCandidate = (
    personId: string,
    jobIds: string[],
    sourceJobId: string,
    newStage: string,
    source: 'cross-submit' | 'cross-add' | 'candidate-search-cross-add' | 'person-add'
) => {
    const endpoint = `${ApiEndpoint}/job/cross-add`;
    return fetch(endpoint, {
        body: JSON.stringify({ sourceJobId, jobIds, personId, newStage, source }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const updatePersonBlacklisted = (id: string, blacklisted: boolean) => {
    const endpoint = `${ApiEndpoint}/person/${id}/blacklisted`;
    return fetch(endpoint, {
        body: JSON.stringify({ blacklisted }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const updateUser = (userId: string, updates: Partial<UserData>) => {
    const endpoint = `${ApiEndpoint}/user/${userId}`;
    return fetch(endpoint, {
        body: JSON.stringify({ updates }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const updatePersonFilename = (personId: string, path: string, filename: string) => {
    const endpoint = `${ApiEndpoint}/person/update-filename`;
    return fetch(endpoint, {
        body: JSON.stringify({ personId, path, filename }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getPersonScheduledMessages = (personId: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/scheduled-messages`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const rescheduleMessage = (
    account: string,
    scheduledMessageId: string,
    newScheduledAt: number,
    personId: string,
    jobId: string
) => {
    const endpoint = `${ApiEndpoint}/scheduled-messages/${account}/reschedule/${scheduledMessageId}`;
    return fetch(endpoint, {
        body: JSON.stringify({ scheduledAt: newScheduledAt, data: { personId, jobId } }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const cancelScheduledMessage = (
    account: string,
    scheduledMessageId: string,
    personId: string,
    jobId: string
) => {
    const endpoint = `${ApiEndpoint}/scheduled-messages/${account}/cancel/${scheduledMessageId}`;
    return fetch(endpoint, {
        body: JSON.stringify({ data: { personId, jobId } }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const impersonate = (email: string) => {
    const endpoint = `${ApiEndpoint}/impersonate/${email}`;
    return fetch(endpoint, {
        headers: getHeaders()
    }).then(parseResponse);
};

export const pauseJobSearches = (jobId: string) => {
    const endpoint = `${ApiEndpoint}/searches/job/${jobId}/pause`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const reassignCandidateAssignee = (personId: string, jobId: string, newAssigneeId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/candidate/${personId}/reassign-assignee`;
    return fetch(endpoint, {
        body: JSON.stringify({ newAssigneeId }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const ignoreContactFromMessage = (account: string, messageId: string) => {
    const endpoint = `${ApiEndpoint}/communication/${account}/ignore-contact-from-message/${messageId}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const crossAddCandidates = (data: { jobId: string; personIds: string[] }) => {
    const endpoint = `${ApiEndpoint}/job/cross-add-multiple`;
    return fetch(endpoint, {
        body: JSON.stringify(data),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getRuntimeConstants = (keys: string[]) => {
    const endpoint = `${ApiEndpoint}/runtime-constants`;
    return fetch(endpoint, {
        body: JSON.stringify({ keys }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getSourcingCampaign = (campaignId: string) => {
    const qs = queryString({ campaignId });
    const endpoint = `${ApiEndpoint}/sourcing/campaign?${qs}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const getSourcer = (sourcerId: string) => {
    const qs = queryString({ sourcerId });
    const endpoint = `${ApiEndpoint}/sourcing/sourcer?${qs}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const fetchCampaignSearchStats = (jobId: string) => {
    const endpoint = `${ApiEndpoint}/searches/campaign/${jobId}/stats`;
    return fetch(endpoint, { headers: getHeaders() }).then(parseResponse);
};

export const addProspects = (data: { campaignId: string; limit: number }) => {
    const endpoint = `${ApiEndpoint}/sourcing/add-prospects`;
    return fetch(endpoint, {
        body: JSON.stringify(data),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const addSourcerMembers = (data: { sourcerId: string; limit: number }) => {
    const endpoint = `${ApiEndpoint}/sourcing/add-sourcer-members`;
    return fetch(endpoint, {
        body: JSON.stringify(data),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const startProfileConflictInvestigation = (conflictId: string, reviewId?: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles/${conflictId}/investigate`;
    return fetch(endpoint, {
        body: JSON.stringify({ reviewId }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const getProfileConflictDetails = (personId1: string, personId2: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles/details`;
    const queryParams = new URLSearchParams({
        personId1,
        personId2
    });
    return fetch(`${endpoint}?${queryParams}`, { headers: getHeaders() }).then(parseResponse);
};

export const splitPerson = (personId: string, name: string) => {
    return fetch(`${ApiEndpoint}/person/${personId}/split`, {
        body: JSON.stringify({ name }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const reassignProfileUrl = (url: string, toPersonId: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles/reassign-url`;
    return fetch(endpoint, {
        body: JSON.stringify({ toPersonId, url }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const reassignContact = (contact: ContactData, toPersonId: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles/reassign-contact`;
    return fetch(endpoint, {
        body: JSON.stringify({ toPersonId, contact }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const reassignFile = (path: string, fromPersonId: string, toPersonId: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles/reassign-file`;
    return fetch(endpoint, {
        body: JSON.stringify({ path, fromPersonId, toPersonId }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const resolveProfileConflict = (personId1: string, personId2: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles`;
    const queryParams = new URLSearchParams({
        personId1,
        personId2
    });
    return fetch(`${endpoint}?${queryParams}`, {
        headers: getHeaders(),
        method: 'delete'
    }).then(parseResponse);
};

export const mergeProfiles = (from: string, to: string) => {
    const endpoint = `${ApiEndpoint}/conflict-profiles/merge-profile`;
    return fetch(endpoint, {
        body: JSON.stringify({ from, to }),
        headers: getHeaders(),
        method: 'post'
    }).then(parseResponse);
};

export const updateProfileLink = (personId: string, url: string, invalid: boolean) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/profile-link`;
    return fetch(endpoint, {
        body: JSON.stringify({ invalid, url }),
        headers: getHeaders(),
        method: 'put'
    }).then(parseResponse);
};

export const deleteProfileLink = (personId: string, url: string) => {
    const endpoint = `${ApiEndpoint}/person/${personId}/profile-link`;
    return fetch(endpoint, {
        body: JSON.stringify({ url }),
        headers: getHeaders(),
        method: 'delete'
    }).then(parseResponse);
};

export const fetchCompanyTalentData = (companyUrl: string, discipline: string) => {
    const qs = queryString({ companyUrl, discipline });
    const endpoint = `${ApiEndpoint}/company/talent-data?${qs}`;
    return fetch(endpoint, {
        headers: getHeaders(),
        method: 'get'
    }).then(parseResponse);
};

export const refreshScoringBrief = (jobId: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/refresh-scoring-brief`;
    return fetch(endpoint, { headers: getHeaders(), method: 'POST' }).catch(() => undefined);
};

export const previewScoringBrief = (jobId: string, scoringBrief: string) => {
    const endpoint = `${ApiEndpoint}/job/${jobId}/preview-scoring-brief`;
    return fetch(endpoint, {
        body: JSON.stringify({ scoringBrief }),
        headers: getHeaders(),
        method: 'POST'
    }).then(parseResponse);
};

// ------------------------ Agent Chat API ------------------------
// Sessions are now managed automatically by conversationId on the backend

type AgentSseExtras<T extends string, P extends Record<string, unknown> = {}> = { type: T } & P & {
        [k: string]: unknown;
    };

export type AgentSSE =
    | AgentSseExtras<'start'>
    | AgentSseExtras<'system_init'>
    | AgentSseExtras<'conversation_created', { conversationId: string; title: string }>
    | AgentSseExtras<'token', { text: string }>
    | AgentSseExtras<'assistant_final', { text: string }>
    | AgentSseExtras<'thinking', { text?: string }>
    | AgentSseExtras<'thinking_delta', { text?: string }>
    | AgentSseExtras<'sdk_event', { event?: string }>
    | AgentSseExtras<'compact_boundary', { id?: string; ts?: number }>
    | AgentSseExtras<'done'>
    | AgentSseExtras<'error', { message?: string }>
    | AgentSseExtras<'aborted', { message?: string }>
    | AgentSseExtras<
          'tool_start',
          {
              id?: string;
              tool_use_id?: string;
              name?: string;
              tool_name?: string;
          }
      >
    | AgentSseExtras<
          'tool_end',
          {
              id?: string;
              tool_use_id?: string;
              name?: string;
              tool_name?: string;
              durationMs?: number;
              status?: string;
              inputPreview?: string;
              outputPreview?: string;
          }
      >
    | AgentSseExtras<
          'result',
          {
              durationMs?: number;
              usage?: {
                  input?: number;
                  inputTokens?: number;
                  input_tokens?: number;
                  output?: number;
                  outputTokens?: number;
                  output_tokens?: number;
                  [k: string]: unknown;
              };
          }
      >
    | AgentSseExtras<'table_data', { data: TableData }>
    | AgentSseExtras<'chart_data', { data: ChartData }>
    | AgentSseExtras<'email_draft', { data: { draftEmailId: string; linkType?: string; stageIndex?: number } }>
    | AgentSseExtras<'submission_draft', { data: { draftSubmissionId: string } }>
    | AgentSseExtras<'client_update_draft', { data: { draftClientUpdateId: string } }>
    | AgentSseExtras<'job_update_draft', { data: { draftJobUpdateId: string } }>
    | AgentSseExtras<'search_preview', { data: { searchDraftId: string } }>;

interface PageContext {
    path: string;
    jobId?: string;
    jobTitle?: string;
    clientId?: string;
    candidateId?: string;
    view?: string;
}

function extractPageContext(): PageContext {
    const path = window.location.pathname;
    const context: PageContext = { path };

    // Extract IDs from URL patterns
    const jobMatch = path.match(/\/job\/([a-z0-9]+)/i);
    const candidateMatch = path.match(/\/candidate\/([a-z0-9]+)/i);
    const clientMatch = path.match(/\/client\/([a-z0-9]+)/i);

    // Add entity IDs and fetch job title from Redux (simple and reliable)
    try {
        if (jobMatch) {
            const jobId = jobMatch[1];
            context.jobId = jobId;

            const state = store.getState();
            const job = state.jobs?.get(jobId);
            if (job?.title) {
                context.jobTitle = job.title;
            }
            if (job?.clientId) {
                context.clientId = job.clientId;
            }
        }

        if (candidateMatch) {
            context.candidateId = candidateMatch[1];
        }

        if (clientMatch) {
            context.clientId = clientMatch[1];
        }
    } catch {
        // Ignore errors accessing store
    }

    // Add view context
    if (path.includes('/board/candidate')) {
        context.view = 'Candidate details';
    } else if (path.includes('/board')) {
        context.view = 'Job board';
    } else if (path.includes('/searches')) {
        context.view = 'Searches';
    }

    return context;
}

const noop = () => {
    /* expected empty */
};

export class StreamTimeoutError extends Error {
    constructor() {
        super('stream_timeout');
        this.name = 'StreamTimeoutError';
    }
}

const STREAM_HEARTBEAT_TIMEOUT_MS = 45000;
const HEARTBEAT_CHECK_INTERVAL_MS = 5000;

export interface AgentChatRuntimeOptions {
    model?: string;
    thinking?: { effort?: 'low' | 'medium' | 'high' | 'xhigh' | 'max' };
}

export const streamAgentChat = async (
    message: string,
    conversationId: string | null,
    onEvent: (evt: AgentSSE) => void,
    signal?: AbortSignal,
    runtimeOptions?: AgentChatRuntimeOptions
): Promise<void> => {
    const endpoint = `${ApiEndpoint}/api/agent/chat/stream`;
    const context = extractPageContext();

    // Get user's timezone from browser (IANA format like "America/Los_Angeles")
    let userTimezone: string | undefined;
    try {
        userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        // Fallback to undefined if timezone detection fails
        userTimezone = undefined;
    }

    const userId = store.getState().session.user.id;
    const body: {
        messages: Array<{ text: string; userId: string }>;
        context: PageContext;
        userTimezone?: string;
        conversationId?: string | null;
        runtimeOptions?: AgentChatRuntimeOptions;
    } = {
        context,
        conversationId,
        messages: [{ text: message, userId }],
        runtimeOptions,
        userTimezone
    };

    const res = await fetch(endpoint, {
        body: JSON.stringify(body),
        headers: Object.assign({}, getHeaders(), { 'Content-Type': 'application/json' }),
        method: 'post',
        signal
    } as any);
    if (!res.ok || !res.body) throw new Error(`agent stream failed: ${res.status}`);

    const reader = (res.body as ReadableStream<Uint8Array>).getReader();
    const decoder = new TextDecoder();

    // Heartbeat timeout: agent sends SSE comment heartbeats every 15s.
    // If we receive nothing for 45s, the connection is dead.
    let lastDataAt = Date.now();
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

    const startHeartbeatMonitor = () => {
        heartbeatTimer = setInterval(() => {
            if (Date.now() - lastDataAt > STREAM_HEARTBEAT_TIMEOUT_MS) {
                clearInterval(heartbeatTimer!);
                heartbeatTimer = null;
                reader.cancel().catch(noop);
            }
        }, HEARTBEAT_CHECK_INTERVAL_MS);
    };

    const clearHeartbeatMonitor = () => {
        if (heartbeatTimer !== null) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
        }
    };

    startHeartbeatMonitor();

    // Incremental SSE parser that avoids re-scanning old buffer content.
    const EVENT_PREFIX = 'event:';
    const DATA_PREFIX = 'data:';
    let buffer = '';
    let event: string | undefined;
    let dataLines: string[] = [];
    let timedOut = false;

    const flush = () => {
        if (!event || dataLines.length === 0) {
            event = undefined;
            dataLines = [];
            return;
        }

        const dataRaw = dataLines.join('\n');
        try {
            const data = JSON.parse(dataRaw);
            const payload = Object.assign({ type: event as AgentSSE['type'] }, data) as AgentSSE;
            onEvent(payload);
        } catch {
            // ignore parse errors for malformed data lines
        }

        event = undefined;
        dataLines = [];
    };

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                // If the reader was cancelled by the heartbeat monitor, it returns done=true
                if (Date.now() - lastDataAt > STREAM_HEARTBEAT_TIMEOUT_MS) {
                    timedOut = true;
                }
                break;
            }

            lastDataAt = Date.now();
            buffer += decoder.decode(value, { stream: true });

            // Process complete lines only; keep a partial line in `buffer`.
            // Invariant: buffer contains only unprocessed content (no previously found newlines).
            let start = 0;
            let nl = buffer.indexOf('\n', start);

            while (nl !== -1) {
                // Extract the line (trim trailing \r for \r\n support)
                let end = nl;
                if (end > 0 && buffer[end - 1] === '\r') end--;
                const line = buffer.slice(start, end);
                start = nl + 1;

                // Blank line terminates an SSE event.
                if (line.length === 0) {
                    flush();
                } else if (line.startsWith(EVENT_PREFIX)) {
                    // Field parsing (SSE spec allows optional space after colon).
                    event = line.slice(EVENT_PREFIX.length).trim();
                } else if (line.startsWith(DATA_PREFIX)) {
                    const raw = line.slice(DATA_PREFIX.length);
                    dataLines.push(raw.startsWith(' ') ? raw.slice(1) : raw);
                }

                // Find next newline
                nl = buffer.indexOf('\n', start);
            }

            // Keep any remaining partial line in buffer (maintains invariant).
            buffer = buffer.slice(start);
        }

        // Flush a final event if the stream ended without a trailing blank line.
        flush();
    } finally {
        clearHeartbeatMonitor();
    }

    if (timedOut) {
        throw new StreamTimeoutError();
    }
};

/**
 * Stop an active agent chat turn.
 * Returns true if a turn was stopped, false if no active turn was found.
 */
export const stopAgentChat = async (conversationId: string): Promise<boolean> => {
    const endpoint = `${ApiEndpoint}/api/agent/chat/stop`;

    try {
        const res = await fetch(endpoint, {
            body: JSON.stringify({ conversationId }),
            headers: getHeaders(),
            method: 'POST'
        });

        if (!res.ok) {
            return false;
        }

        const data = await res.json();
        return data?.stopped === true;
    } catch {
        return false;
    }
};

export const getAgentChatStatus = async (conversationId: string): Promise<{ active: boolean } | null> => {
    const endpoint = `${ApiEndpoint}/api/agent/chat/status/${encodeURIComponent(conversationId)}`;

    try {
        const res = await fetch(endpoint, {
            headers: getHeaders(),
            method: 'GET'
        });

        if (!res.ok) {
            return null;
        }

        return await res.json();
    } catch {
        return null;
    }
};
